import { useMemo } from "react";
import { noop } from "lodash";
import { useFormContext } from "react-hook-form";
import { useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import type { ButtonVariant } from "@/react/components/shared/uikit/Button";
import type { DropdownProps } from "@/react/components/shared/uikit/Dropdown";
import { QUERY_KEY } from "@/react/hooks/usePaywallsInfiniteQuery";
import type { PaywallStatus } from "@/react/types";
import { useChangePaywallStates } from "@circle-react/components/shared/Paywalls/hooks/useChangePaywallStates";
import { isEditingMode } from "@circle-react/helpers/paywallHelpers";
import { PAYWALL_STATUS } from "@circle-react/helpers/paywallHelpers";
import { tabs } from "../../../constants";
import type { PaywallForm } from "../../../types/PaywallForm";
import { usePaywallFormContext } from "../../PaywallFormProvider";

interface UseModalFooterProps {
  activeTab: string;
  currentPaywallStatus?: PaywallStatus;
}

interface ButtonVariantProps {
  buttonLabel: string;
  onClick: () => void;
  variant: ButtonVariant;
}

const generatePrimaryButtonStatesByPaywallStatus = (
  isLastStep: boolean,
  submitAsPublish: () => void,
): Record<PaywallStatus | "paywallCreation", ButtonVariantProps> => ({
  active: {
    buttonLabel: t("settings.paywalls.modal.footer.save"),
    variant: "circle",
    onClick: noop,
  },
  draft: {
    onClick: noop,
    variant: "circle",
    buttonLabel: t("settings.paywalls.modal.footer.save"),
  },
  inactive: {
    onClick: noop,
    variant: "circle",
    buttonLabel: t("settings.paywalls.modal.footer.save"),
  },
  paywallCreation: {
    variant: "circle",
    buttonLabel: isLastStep
      ? t("settings.paywalls.modal.footer.publish")
      : t("settings.paywalls.modal.footer.next"),
    // isLastStep is needed because this button is used
    // to navigate between tabs
    onClick: () => isLastStep && submitAsPublish(),
  },
});

export const useModalFooter = ({
  activeTab,
  currentPaywallStatus,
}: UseModalFooterProps) => {
  const { onClose } = usePaywallFormContext();
  const { register, setValue, watch, getValues, formState } =
    useFormContext<PaywallForm>();
  const queryClient = useQueryClient();
  const paywall = getValues();

  const { publish, archive, remove, masquerade } = useChangePaywallStates(
    paywall,
    () => {
      void queryClient.refetchQueries([QUERY_KEY]);
      onClose();
    },
  );

  const isLastStep = useMemo(
    () => activeTab === tabs.TRACKING_TAB,
    [activeTab],
  );

  const submitPublishAs = (shouldPublish: boolean) => {
    register("publish");
    setValue("publish", shouldPublish);
  };

  const submitAsPublish = () => submitPublishAs(true);
  const submitAsUnpublish = () => {
    submitPublishAs(false);
    register("status");
    setValue("status", PAYWALL_STATUS.INACTIVE);
  };

  const primaryButtonStatesByPaywallStatus = useMemo(
    () =>
      generatePrimaryButtonStatesByPaywallStatus(isLastStep, submitAsPublish),
    [isLastStep, submitAsPublish],
  );

  const secondaryButtonStatesByPaywallStatus: Record<
    PaywallStatus | "paywallCreation",
    ButtonVariantProps
  > = {
    active: {
      onClick: submitAsUnpublish,
      variant: "danger",
      buttonLabel: t("settings.paywalls.modal.footer.unpublish"),
    },
    draft: {
      onClick: submitAsPublish,
      variant: "secondary",
      buttonLabel: t("settings.paywalls.modal.footer.publish"),
    },
    inactive: {
      onClick: submitAsPublish,
      variant: "secondary",
      buttonLabel: t("settings.paywalls.modal.footer.publish"),
    },
    paywallCreation: {
      onClick: noop,
      variant: "secondary",
      buttonLabel: t("settings.paywalls.modal.footer.save_draft"),
    },
  };

  const currentSecondaryButtonState =
    (currentPaywallStatus &&
      secondaryButtonStatesByPaywallStatus[currentPaywallStatus]) ??
    secondaryButtonStatesByPaywallStatus.paywallCreation;

  const currentPrimaryButtonState =
    (currentPaywallStatus &&
      primaryButtonStatesByPaywallStatus[currentPaywallStatus]) ??
    primaryButtonStatesByPaywallStatus.paywallCreation;

  const isCreatingAndLastStep =
    !isEditingMode(currentPaywallStatus) && isLastStep;
  const isDestroyable = watch("destroyable");
  const isActive = currentPaywallStatus === PAYWALL_STATUS.ACTIVE;

  const buildOptions = () => {
    const options: DropdownProps["options"] = [];

    if (!isEditingMode(currentPaywallStatus)) {
      return options;
    }

    options.push({
      label: t(["actions"]),
      value: t(["actions"]),
      hide: true,
    });

    if (isActive) {
      options.push({
        label: t(["archive"]),
        value: t(["archive"]),
        onClick: archive,
        pathTo: "#",
      });
    }

    if (!isActive) {
      options.push({
        label: t(["publish"]),
        value: t(["publish"]),
        onClick: publish,
        pathTo: "#",
      });
    }

    if (isDestroyable) {
      options.push({
        label: t(["delete"]),
        value: t(["delete"]),
        onClick: remove,
        color: "text-v2-danger",
        pathTo: "#",
      });
    }

    options.push({
      label: t(["view_as"]),
      value: t(["view_as"]),
      onClick: masquerade,
      pathTo: "#",
    });
    return options;
  };

  const options = useMemo(buildOptions, [
    archive,
    publish,
    remove,
    masquerade,
    isDestroyable,
    isActive,
    currentPaywallStatus,
  ]);

  return {
    isEditingMode: isEditingMode(currentPaywallStatus),
    currentPrimaryButtonState,
    currentSecondaryButtonState,
    isCreatingAndLastStep,
    options,
    formState,
  };
};
